/** @format */

import { useFormik } from "formik";
import React, { type FC, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import ReactDOM from "react-dom";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  useAuthContext,
  useGlobalState,
  useSchemaValidation,
} from "senf-shared";
import { toSentenceCase } from "senf-shared/utils/convertCase";
import i18n from "senf-shared/utils/i18n";
import { fetchProjectSpecificData } from "../../../util/demographicUtils";
import Input from "../../atoms/inputs/Input";
import Button from "../../core/button/Button";
import DemographicsForm from "../demographics/DemographicsForm";
import type { AuthAddDetailsProps } from "./AuthAddDetails.types";

// Custom hook for form logic
const useAuthDetailsForm = (user: any, project: any) => {
  const { addUserDetails } = useSchemaValidation();
  const [translation] = useGlobalState().translationsState;

  const [initialValues, setInitialValues] = useState({
    handle: user?.isAnonymous ? "Gastnutzer" : user?.handle || "",
    postcode: user?.postcode || "",
    age: user?.age || "",
    gender: user?.gender || "",
    newsletter: user?.newsletter || false,
  });

  useEffect(() => {
    const loadProjectSpecificData = async () => {
      const participantData = await fetchProjectSpecificData(project, user);
      if (participantData) {
        setInitialValues((prevValues) => ({
          ...prevValues,
          ...participantData,
        }));
      }
    };
    loadProjectSpecificData();
  }, [project, user]);

  const formik = useFormik({
    initialValues,
    validationSchema: addUserDetails(project, translation),
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => {
      console.log("submit");
    },
  });

  // Auto-touch fields when they have values
  useEffect(() => {
    const fieldsToTouch: Record<string, boolean> = {};

    if (formik.values.handle) fieldsToTouch.handle = true;
    if (formik.values.postcode) fieldsToTouch.postcode = true;

    if (Object.keys(fieldsToTouch).length > 0) {
      formik.setTouched((prev) => ({ ...prev, ...fieldsToTouch }));
    }
  }, [formik.values.handle, formik.values.postcode]);

  const getDisabledNotice = () => {
    try {
      return toSentenceCase(
        new Intl.ListFormat(i18n.resolvedLanguage, {
          style: "long",
          type: "conjunction",
        }).format(Object.values(formik?.errors || {})),
      );
    } catch (error) {
      return "global:fillInAllFields";
    }
  };

  const isFormValid =
    formik.isValid && formik.values.age && formik.values.gender;

  return {
    formik,
    isFormValid,
    getDisabledNotice,
  };
};

// Component for navigation buttons
const SubmitButtonWrapper: FC<{
  variant?: string;
  onSave: () => void;
  onPrev?: () => void;
  isLoading: boolean;
  isDisabled: boolean;
  disabledNotice: string;
}> = ({ variant, onSave, onPrev, isLoading, isDisabled, disabledNotice }) => {
  const { t } = useTranslation();

  const SaveButton = (
    <Button
      className="w-full"
      size={variant === "post" ? "lg" : "xl"}
      variant="primary"
      width="max"
      onClick={onSave}
      loading={isLoading}
      disabled={isDisabled}
      disabledNotice={disabledNotice}
      text={
        variant === "post" ? t("dialogMap:add.shareIdea") : t("global:save")
      }
    />
  );

  const buttonContent =
    variant === "post" && onPrev ? (
      <div className="flex justify-between gap-3 w-full">
        <Button
          size="lg"
          leadingIcon="arrowhead-left-md"
          aria-label={t("global:previous")}
          onClick={onPrev}
        />
        {SaveButton}
      </div>
    ) : (
      SaveButton
    );

  // For post variant, make buttons sticky on mobile using portal
  if (variant === "post") {
    const stickyButtons = (
      <div className="fixed bottom-0 left-0 right-0 p-5 border-t bg-white z-[9999999]">
        {buttonContent}
      </div>
    );

    // Use portal on mobile to break out of modal/drawer context
    if (isMobile) {
      return ReactDOM.createPortal(stickyButtons, document.body);
    }

    // On desktop, just return the buttons normally
    return <div className="w-full pb-5">{buttonContent}</div>;
  }

  return <div className="w-full pb-5">{buttonContent}</div>;
};

const AuthAddDetails: FC<AuthAddDetailsProps> = ({
  user,
  project,
  variant,
  onAfterSave,
  onPrev,
}) => {
  const { t } = useTranslation();
  const auth = useAuthContext();
  const { formik, isFormValid, getDisabledNotice } = useAuthDetailsForm(
    user,
    project,
  );

  const handleSave = async () => {
    try {
      const updatedUser = await auth?.submitEditDetails?.({
        formikStore: formik,
        project,
      });
      onAfterSave?.(updatedUser || user);
    } catch (error) {
      console.error("Failed to save user details:", error);
    }
  };

  const isLoading = auth?.loading === "edit";
  const isDisabled = !isFormValid || isLoading;

  const showUsernameField = !user?.isAnonymous && variant === "editProfile";
  const showPrivacyInfo = variant !== "editProfile";

  return (
    <div
      className={`relative flex flex-col pt-5 ${variant === "post" && isMobile ? "pb-24" : ""}`}
    >
      <span className="heading-xl">{t("auth:addDetails.title")}</span>

      <div className="my-8 flex flex-col gap-4">
        {showUsernameField && (
          <Input
            name="handle"
            type="text"
            placeholder={t("global:username")}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.handle}
            error={formik.touched.handle && Boolean(formik.errors.handle)}
            note={formik.touched.handle && formik.errors.handle}
            label={t("global:username")}
          />
        )}

        <DemographicsForm
          formik={formik}
          project={project}
          user={user}
          isAuthFlow={true}
        />

        {showPrivacyInfo && (
          <span className="body-sm text-text-subdued">
            <Trans i18nKey="auth:demographicDataInfo">
              ...
              <span className="body-sm-bold">...</span>
              ...
              <Link
                to={`/projects/${project?.projectId}/privacyPolicy`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "underline", fontWeight: "500" }}
              />
              ...
            </Trans>
          </span>
        )}
      </div>

      <SubmitButtonWrapper
        variant={variant}
        onSave={handleSave}
        onPrev={onPrev}
        isLoading={isLoading}
        isDisabled={isDisabled}
        disabledNotice={getDisabledNotice()}
      />
    </div>
  );
};

export default AuthAddDetails;
